/* eslint-disable max-lines */
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { isMobileOnly, isTablet } from 'react-device-detect';
import Captcha from 'react-google-recaptcha';
import classNames from 'classnames';
import { StatusCodes } from 'http-status-codes';
import _debounce from 'lodash.debounce';

import NotificationService from 'services/NotificationService';
import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import ControlledFeatureService from 'services/ControlledFeatureService';
import CookieStorageService from 'services/CookieStorageService';
import EpayService from 'services/EpayService/EpayService';
import GuestBillingService from 'services/GuestBillingService';
import { translate } from 'services/i18nService';
import reCAPTCHAv3Service from 'services/reCAPTCHAv3Service';
import trackingService from 'services/UITrackingService';
import UserStore from 'stores/UserStore';
import {
  API_ERRORS,
  apiValidateFieldNew,
} from 'pages/Studio/ApiCalls';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import Input from 'components/Input/Input';
import Button from 'components/Button/Button';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import controlledFeatures from 'enum/controlledFeatures';
import {
  MAX_USERNAME_LENGTH,
  MIN_USERNAME_LENGTH,
  SPECIAL_CHARS_PASSWORD,
} from 'enum/inputs';
import reCAPTCHAv3Action from 'enum/reCAPTCHAv3Action';
import routeDefinitions from 'enum/routeDefinitions';
import { SIGN_UP_TYPE } from 'enum/helpers';
import {
  MINUTE_AS_MILLISECONDS,
  SECOND_AS_MILLISECONDS,
} from 'enum/timeFormat';
import allUserGroups from 'enum/userGroups';
import {
  generateID,
  passwordErrorMsgSignIn,
} from 'utils/helpers';
import parseCatch from 'utils/parseCatch';
import ModalService from 'services/ModalService';
import fanClubService from 'services/fanClub';
import {
  actionCreators as appActions,
  selector as appSelector,
} from 'app/AppState';
import { RegistrationModalCaller } from 'pages/RegistrationModal/utils';

import {
  actionCreators as listActions,
  selector,
} from '../State';

import styles from './RegistrationForm.css';


const TYPO_SERVICE_TIMEOUT = SECOND_AS_MILLISECONDS;
const VALIDATE_FIELD_DELAY = 400;
const EXPIRY_DATE_IN_MINUTES = 90;
const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const DEVICES = {
  mobile: 'mobile',
  tablet: 'tablet',
  desktop: 'desktop',
};

@connect(selector, (dispatch) => {
  return { actions: bindActionCreators(listActions, dispatch) };
})
@connect(appSelector, (dispatch) => {
  return { appActions: bindActionCreators(appActions, dispatch) };
})
class RegistrationForm extends PureComponent {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    appActions: PropTypes.object.isRequired,
    genderPreference: PropTypes.string.isRequired,
    changeRegisterStep: PropTypes.func,
    closeGoldRegistrationModal: PropTypes.func,
    elementClickedId: PropTypes.string,
    membership: PropTypes.object,
    profileName: PropTypes.string,
    signUpType: PropTypes.string,
    user: PropTypes.object,
    onAfterRegister: PropTypes.func,
  };

  static defaultProps = {
    changeRegisterStep: undefined,
    closeGoldRegistrationModal: undefined,
    elementClickedId: undefined,
    membership: undefined,
    profileName: undefined,
    signUpType: undefined,
    user: undefined,
    onAfterRegister: undefined,
  };

  constructor(...args) {
    super(...args);

    const { user, signUpType } = this.props;
    const specialOffers = true;
    const hasRepeatEmail = ControlledFeatureService.setUser(user).has(controlledFeatures.REPEAT_EMAIL_CHECK);
    const isRepeatEmailActive = hasRepeatEmail && signUpType !== SIGN_UP_TYPE.GOLD;

    this.state = {
      username: { value: '', isValid: true },
      password: { value: '', isValid: true, errorMessage: '' },
      email: {
        value: user?.email || '',
        isValid: true,
      },
      isDomainMX: false,
      repeatEmail: {
        value: '',
        isValid: true,
      },
      specialOffers,
      isRepeatEmailActive,
      captcha: null,
      showUsernameSuggestionPoppup: false,
      usernameSuggestions: [],
      tooltipOpen: true,
      apiError: {
        username: null,
        email: null,
      },
      formState: 'idle',
      showCaptchaV2: false,
      captchaV2Verified: false,
      showSubmitButton: false,
    };

    this.IDS = {
      prefix: 'DM145ap2',
      element: 'registrationForm',
      regForm: 'regForm',
      joinCAM4Btn: 'joinCAM4Btn',
    };

    this.makeId = generateID(this.IDS.prefix, this.IDS.element);
    this.debounceValidateField = _debounce(this.validateField, VALIDATE_FIELD_DELAY);
    this.reCAPTCHAv3Instance = null;
  }

  componentDidMount() {
    if (this.shouldUseReCAPTCHAv3()) {
      this.setState(
        { showSubmitButton: true },
        () => {
          const reCaptchaKey = this.props.user.user.getIn(['user', 'pK3']);
          this.reCAPTCHAv3Instance = new reCAPTCHAv3Service();
          this.reCAPTCHAv3Instance.init(reCaptchaKey, reCAPTCHAv3Action.REGISTRATION);
        }
      );
    } else if (this.shouldUseReCAPTCHAv2()) {
      this.setState({
        showCaptchaV2: true,
        showSubmitButton: false,
      });
    } else {
      this.setState({ showSubmitButton: true });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.elementClickedId !== this.props.elementClickedId && this.state.usernameSuggestions.length > 0) {
      this.setState({
        tooltipOpen:
          (
            this.props.elementClickedId === 'tooltipId' ||
            this.props.elementClickedId === 'registration-username'
          ),
      });
    }

    // hide captchaV2 after delay (wait for check animation to be done at captcha widget)
    if (prevState.captchaV2Verified !== this.state.captchaV2Verified && this.state.captchaV2Verified) {
      this.hideCaptchaTimeout = setTimeout(() => {
        this.setState(() => {
          return {
            showSubmitButton: true,
            showCaptchaV2: false,
          };
        });
      }, SECOND_AS_MILLISECONDS);
    }
  }

  componentWillUnmount() {
    this.setState({ token: null });

    if (this.reCAPTCHAv3Instance) {
      this.terminateReCAPTCHAv3Instance();
    }

    // clear hideCaptchaTimeout
    if (this.hideCaptchaTimeout) {
      clearTimeout(this.hideCaptchaTimeout);
    }
  }

  terminateReCAPTCHAv3Instance = () => {
    if (this.reCAPTCHAv3Instance) {
      this.reCAPTCHAv3Instance.terminate();
      this.reCAPTCHAv3Instance = null;
    }
  };

  shouldUseReCAPTCHAv3 = () => {
    const captchav3 = ControlledFeatureService.setUser(this.props.user).has(
      allUserGroups.GOOGLE_CAPTCHA_V3_CREATE_ACCOUNT
    );

    return captchav3;
  };

  shouldUseReCAPTCHAv2 = () => {
    const captchav2 = ControlledFeatureService.setUser(this.props.user).has(
      allUserGroups.GOOGLE_CAPTCHA_CREATE_ACCOUNT
    );

    return captchav2 || this.shouldUseReCAPTCHAv3();
  };

  handleGuestBillingToGoldRegistration = () => {
    const {
      actions,
      closeGoldRegistrationModal,
      signUpType,
      profileName,
    } = this.props;
    const { username, password } = this.state;
    const { joinMigratedFanclub } = fanClubService;


    GuestBillingService.register(username.value, password.value)
      .then((res) => {
        if (res.status === StatusCodes.OK && res.data.status === 'SUCCESS') {
          actions.getUserState();
          if (signUpType === SIGN_UP_TYPE.FANCLUB) {
            const params = {
              accessHash: res.data.accessHash,
              performer: profileName,
              username: username.value,
            };
            joinMigratedFanclub(params);
          } else {
            this.handleGoldRegistration(null, res.data.accessHash);
          }
          this.setState({ formState: 'idle' });
          closeGoldRegistrationModal();
        }
      })
      .catch((err) => {
        if (err !== '' && typeof err !== 'string') {
          const field = this.getFieldNameBasedOnErrorCode(err.code);
          if (field !== 'password') {
            this.setApiErrorState(field, err);
          } else {
            this.onPasswordChange(field, {}, true);
          }
        }
        this.setState({ formState: 'idle' });
      });
  };

  trackRegistrationStatus = (status, error = null) => {
    trackingService.emit(
      'submitRegistrationOverlay',
      {
        location: window.location.pathname,
        username: String(this.state.username.value),
        email: String(this.state.email.value),
        specialOffers: Boolean(this.state.specialOffers),
        reason: error?.message?.toString?.() || null,
        success: status,
      },
    );
  };

  submitHandler = () => {
    const { signUpType, user, genderPreference } = this.props;
    const { username, password, email, specialOffers, captcha } = this.state;
    const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
    const isGoldOrFanClubSignUp = signUpType === SIGN_UP_TYPE.GOLD || signUpType === SIGN_UP_TYPE.FANCLUB;
    const registrationParams = {
      username: username.value,
      password: password.value,
      email: email.value,
      signUpType,
      promotions: specialOffers,
      captcha,
      useV3: false,
      genderPreference,
    };

    if (this.shouldUseReCAPTCHAv3()) {
      this.setState({ formState: 'loading' });
    } else {
      this.setState({
        formState: 'loading',
        showCaptchaV2: false,
        showSubmitButton: true,
      });
    }

    if (isGoldOrFanClubSignUp) {
      if (isGuestBilling) {
        this.handleGuestBillingToGoldRegistration();

        return;
      }

      this.handleRegistration(registrationParams)
        .then((res) => {
          this.handleGoldRegistrationResponse(res);
          this.trackRegistrationStatus(true);
        })
        .catch((err) => {
          this.handleRegistrationError(err);
          this.trackRegistrationStatus(false, err);
        });

      return;
    }

    if (this.reCAPTCHAv3Instance) {
      this.handleRegistrationWithReCAPTCHAv3(
        username.value,
        password.value,
        email.value,
        signUpType,
        specialOffers,
        genderPreference
      )
        .then((res) => {
          this.terminateReCAPTCHAv3Instance();
          if (isGoldOrFanClubSignUp) {
            this.handleGoldRegistrationResponse(res);
          } else {
            this.handleDefaultRegistrationResponse();
          }

          this.trackRegistrationStatus(true);
        })
        .catch((error) => {
          this.handleRegistrationWithReCAPTCHAv3Error(error);
          this.trackRegistrationStatus(false, error);
        });

      return;
    }

    this.handleRegistration(registrationParams)
      .then(() => {
        this.handleDefaultRegistrationResponse();
        this.trackRegistrationStatus(true);
      })
      .catch((err) => {
        this.handleRegistrationError(err);
        this.trackRegistrationStatus(false, err);
      });
  };

  handleRegistrationWithReCAPTCHAv3 = (username, password, email, signUpType, promotions, genderPreference) =>
    this.reCAPTCHAv3Instance
      .getToken()
      .then((recaptchaToken) =>
        this.handleRegistration(
          username,
          password,
          email,
          signUpType,
          promotions,
          recaptchaToken,
          true,
          genderPreference
        )
      );

  handleRegistrationWithReCAPTCHAv3Error = () => {
    this.setState(
      {
        formState: 'idle',
        showCaptchaV2: true,
        showSubmitButton: false,
      },
      () => {
        this.terminateReCAPTCHAv3Instance();
      }
    );
  };

  handleDefaultRegistrationResponse = () => {
    const { changeRegisterStep, onAfterRegister } = this.props;
    const { email } = this.state;

    const { handleResendEmailValue } = UserStore;

    this.setRegistrationPathCookie();
    this.setState({ formState: 'idle' });
    handleResendEmailValue(email.value);
    changeRegisterStep();
    onAfterRegister?.();
  };

  handleGoldRegistrationResponse = (response) => {
    const { changeRegisterStep, signUpType, profileName } = this.props;
    const { email } = this.state;
    const { jwt, username } = response;
    const { handleResendEmailValue } = UserStore;
    const { joinMigratedFanclub } = fanClubService;
    const params = {
      username,
      jwt,
      performer: profileName,
    };

    this.setRegistrationPathCookie();
    if (signUpType === SIGN_UP_TYPE.FANCLUB) {
      joinMigratedFanclub(params);
    } else {
      this.handleGoldRegistration(jwt);
    }
    this.setState({ formState: 'idle' });
    handleResendEmailValue(email.value);
    changeRegisterStep();
  };

  setRegistrationPathCookie = () => {
    const expires =
      new Date(new Date().getTime() + (EXPIRY_DATE_IN_MINUTES * MINUTE_AS_MILLISECONDS));
    let isAffiliate = false;
    if (
      location.search.indexOf('act=voluum_') !== -1 ||
      location.search.indexOf('act=hasOffers_') !== -1 ||
      location.search.indexOf('act=200') !== -1
    ) {
      isAffiliate = true;
    }
    const data = {
      path: location.pathname,
      isAffiliate,
    };
    CookieStorageService.set('cam4-PrevRegPath', JSON.stringify(data), {
      path: '/',
      expires,
    });
  };

  handleRegistration = ({ username, password, email, signUpType, promotions, captcha, useV3, genderPreference }) =>
    this.props.actions.newUserRegistration(
      username,
      password,
      email,
      signUpType,
      promotions,
      captcha,
      useV3,
      genderPreference
    );

  handleRegistrationError = (err) => {
    if (err !== '' && typeof err !== 'string') {
      const field = this.getFieldNameBasedOnErrorCode(err.code);
      if (field !== 'password') {
        this.setApiErrorState(field, err);
      } else {
        this.onPasswordChange(field, {}, true);
      }
    }
    this.setState({
      formState: 'idle',
      showCaptchaV2: true,
      showSubmitButton: false,
      captchaV2Verified: false,
    });
  };

  handleGoldRegistration = (jwt, accessHash) => {
    const {
      membership,
      user: { user },
    } = this.props;
    const { username, email } = this.state;
    const activeCampaigns = user.get('user').get('activeCampaigns');
    const activeCampaign = activeCampaigns && activeCampaigns.size && activeCampaigns.get(0);
    const campaignDetails = activeCampaign && activeCampaign.get('campaignDetails');
    const showXSell = campaignDetails && campaignDetails.get('showXSell');
    let currentDevice = '';
    if (isMobileOnly) {
      currentDevice = DEVICES.mobile;
    } else if (isTablet) {
      currentDevice = DEVICES.tablet;
    } else {
      currentDevice = DEVICES.desktop;
    }

    const options = {
      originalDomain: BASE_URL || location.origin,
      siteSubcat: 'cam4.com',
      returnPath: routeDefinitions.checkout,
      device: currentDevice,
      email: email.value,
      paymentType: membership.categories[0].methods[0].type,
      userAgent: '',
    };

    if (jwt) {
      options.jwt = jwt;
    }

    if (accessHash) {
      options.accessHash = accessHash;
    }

    if (showXSell) {
      options.showXSell = showXSell;
    }

    const { processorId } = membership.categories[0].methods[0];
    EpayService.openPaymentProcessorWindow(processorId);
    EpayService.getPaymentProcessorData(processorId, membership.id, username.value, options)
      .then((res) => {
        if (res.status === StatusCodes.OK && res.data?.nextStage) {
          EpayService.prepareForm(res.data.nextStage);
          try {
            EpayService.submitForm();
          } catch (error) {
            EpayService.submitForm();
          }
        }
      })
      .catch((err) => parseCatch(err, true, true));
  };

  setApiErrorState = (fieldName, value) => {
    switch (fieldName) {
      case 'username':
        this.setState(({ apiError, username }) => {
          return {
            showUsernameSuggestionPoppup: !(value === null || value.suggestedUsernames.length === 0),
            usernameSuggestions:
              value === null || value.suggestedUsernames.length === 0 ? [] : value.suggestedUsernames.splice(0, 3),
            tooltipOpen: !(value === null || value.suggestedUsernames.length === 0),
            apiError: {
              ...apiError,
              [fieldName]: value === null ? 'username_ok' : value.code,
            },
            username: {
              ...username,
              isValid: value === null,
            },
          };
        });
        this.onPasswordChange('password', {}, true);
        break;

      case 'email':
        this.setState(({ apiError, email }) => {
          return {
            apiError: {
              ...apiError,
              [fieldName]: value === null ? 'email_ok' : value.code,
            },
            email: {
              ...email,
              isValid: value === null,
              value: email.value.trim(),
            },
          };
        });
        break;

      default:
    }
  };

  validateField = (fieldName, value) => {
    apiValidateFieldNew(
      fieldName,
      fieldName === 'email' ? value.trim() : value.trim(),
      () => {
        this.setApiErrorState(fieldName, null);
      },
      (error) => {
        this.setApiErrorState(fieldName, error);
      }
    );
  };

  getFieldNameBasedOnErrorCode = (errorCode) => {
    let fieldName = '';
    switch (errorCode) {
      case 'invalid_password':
        fieldName = 'password';
        break;

      case 'no_username':
      case 'invalid_username':
      case 'username_exists':
      case 'username_censored':
      case 'invalid_numbers':
        fieldName = 'username';
        break;

      case 'invalid_email':
      case 'invalid_domain':
      case 'email_exists':
      case 'USER_FAN':
        fieldName = 'email';
        break;

      default:
    }

    return fieldName;
  };

  getFieldErrorText = (fieldName) => {
    const { apiError } = this.state;

    const errorCode = apiError[fieldName];
    let errorMsg = '';

    if (API_ERRORS[fieldName][errorCode]) {
      errorMsg = translate(API_ERRORS[fieldName][errorCode], {
        0: MIN_USERNAME_LENGTH,
        1: MAX_USERNAME_LENGTH,
      });
    }

    return errorMsg;
  };

  comparingEmails = () => {
    const { repeatEmail } = this.state;

    if (!repeatEmail.value) {
      return '';
    }

    if (!repeatEmail.isValid) {
      return translate('homepage.v2.studio.errors.repeatEmail.unmatch');
    }

    if (repeatEmail.isValid) {
      return translate('homepage.v2.studio.correct.repeatEmail.match');
    }

    return '';
  };

  handleUsernameSuggestionClick = (e) => {
    e.stopPropagation();
    this.onFieldChange('username', e);
    this.setState({
      showUsernameSuggestionPoppup: false,
      usernameSuggestions: [],
    });
  };

  handlePromotions = () => {
    this.setState((state) => {
      return { specialOffers: !state.specialOffers };
    });
  };

  handleCaptchaChange = (token) => {
    if (token === '') {
      return;
    }

    this.setState({ captcha: token, captchaV2Verified: true },
      () => {
        this.submitHandler();
      });
  };

  createUsernameSuggestionsList = (suggestions) =>
    suggestions.map((el, index) => (
      <li key={el + index} className={styles.usernameListElement} onClick={this.handleUsernameSuggestionClick}>
        {el}
      </li>
    ));

  captchaV2View = () => {
    const reCaptchaKey = this.props.user.user.getIn(['user', 'pK']);

    const captchaClasses = classNames({
      [styles.textSm]: true,
      [styles.alignCenter]: true,
      [styles.captchaWrap]: true,
    });

    const visibleByFeature = this.shouldUseReCAPTCHAv2();

    if (!visibleByFeature) {
      return null;
    }

    return (
      <div className={captchaClasses}>
        <Captcha sitekey={reCaptchaKey} size="normal" onChange={this.handleCaptchaChange} />
      </div>
    );
  };

  isBtnDisabled = () => {
    const { isRepeatEmailActive, repeatEmail, email, username, password, isDomainMX } = this.state;
    let areValid = true;

    ['password', 'username', 'email'].forEach((field) => {
      const { value, isValid } = this.state[field];

      const isNotEmpty = value !== '';
      const hasMinLength = value.length > 3;

      areValid = areValid && isNotEmpty && hasMinLength && isValid;
    });

    if (username.value === password.value) {
      areValid = false;
    }

    if (isRepeatEmailActive && email.value !== repeatEmail.value) {
      areValid = false;
    }

    if (isDomainMX) {
      areValid = false;
    }

    return !areValid;
  };

  toggleTooltip = () => {
    this.setState((prevState) => {
      return { tooltipOpen: !prevState.tooltipOpen };
    });
  };

  onValidateSuccessCorrectedEmail = (correctEmail) => () => {
    this.setState((prevState) => {
      const { apiError, email } = prevState;

      return {
        apiError: {
          ...apiError,
          email: 'email_ok',
        },
        email: {
          ...email,
          isValid: true,
          value: correctEmail,
        },
      };
    }, () => {
      this.submitHandler();
    });
  };

  onValidateErrorCorrectedEmail = (correctEmail) => (err) => {
    this.setState((prevState) => {
      const { apiError, email } = prevState;

      return {
        apiError: {
          ...apiError,
          email: err.code,
        },
        email: {
          ...email,
          isValid: false,
          value: correctEmail,
        },
      };
    });
  };

  onSubmitForm = async(event) => {
    const { email: { value } } = this.state;

    if (event) {
      event.preventDefault();
    }

    const EMAIL_DOMAIN_TYPO_URL = ConfigurationService.get(ConfigurationKeys.EMAIL_DOMAIN_TYPO_URL);
    const url = new URL(`${EMAIL_DOMAIN_TYPO_URL}/api/valid-domains`);
    const currentDomain = value.replace(/.*@/, '');
    url.searchParams.set('domain', currentDomain);

    let response = undefined;
    try {
      response = await ApiService.get(url, {
        withCredentials: false,
        timeout: TYPO_SERVICE_TIMEOUT,
      });
    } catch (err) {
      this.submitHandler();
    }

    const validDomain = response?.data?.is_valid;
    const notValidReason = response?.data?.reason;
    if (validDomain) {
      this.submitHandler();

      return;
    }

    if (notValidReason && notValidReason === 'typo') {
      const correctDomain = response?.data?.valid_domain;
      const correctEmail = value.replace(/@(.+)/, `@${correctDomain}`).trim();
      apiValidateFieldNew(
        'email',
        correctEmail,
        this.onValidateSuccessCorrectedEmail(correctEmail),
        this.onValidateErrorCorrectedEmail(correctEmail),
      );
    }

    if (notValidReason && notValidReason === 'mx') {
      NotificationService.create({
        type: NotificationService.type.TOAST,
        subtype: NotificationService.subtype.ERROR,
        text: translate('account.registration.invalid.domain'),
      });
      this.setState({ isDomainMX: true });
    }

  };

  onPasswordChange = (fieldName, event, generalError = false) => {
    const value = generalError ? this.state.password.value.trim() : event.target.value.trim();
    const changed = true;
    const res = passwordErrorMsgSignIn(value, this.state.username.value);
    const { isValid } = res;
    let errorMessage = '';

    switch (res.text) {
      case 'profile.change.password.jsp.4':
        errorMessage = translate('help.help.password.error');
        break;

      case 'profile.profile.password.error.specialchar':
        errorMessage = translate(res.text, { 0: SPECIAL_CHARS_PASSWORD });
        break;

      default:
        errorMessage = translate(res.text);
        break;
    }

    this.setState({ [fieldName]: { value, isValid, errorMessage, changed } });
  };

  onPasswordInputChange = (event) => {
    this.onPasswordChange('password', event);
  };

  onFieldChange = (fieldName, event) => {
    const value = Number.isInteger(event.target.value) ? event.target.innerHTML : event.target.value;
    const isValid = false;
    const { apiError } = this.state;
    const trimedValue = value.trim();

    const newState = {
      [fieldName]: { value: trimedValue, isValid },
      apiError,
    };

    if (Object.hasOwn(API_ERRORS[fieldName], apiError[fieldName])) {
      newState.apiError[fieldName] = fieldName === 'username' && value.length < 4 ? 'invalid_username' : null;
      if (fieldName === 'username' && value.length < 4) {
        newState.showUsernameSuggestionPoppup = false;
      }
    }

    this.setState(newState);

    if (!isValid && (value.length === 0 || value.length > 3)) {
      this.debounceValidateField(fieldName, value);
    }
  };

  onUsernameChange = (event) => {
    this.onFieldChange('username', event);
  };

  onEmailChange = (event) => {
    const { value } = event.target;
    const { repeatEmail, showCaptchaV2 } = this.state;

    if (!showCaptchaV2) {
      this.onFieldChange('email', event);
      this.setState({ isDomainMX: false });
    }

    if (!repeatEmail.value) {
      return;
    }

    this.setState({
      repeatEmail: {
        value: this.state.repeatEmail.value,
        isValid: this.state.repeatEmail.value === value.trim(),
      },
    });
  };

  onRepeatEmailPaste = (ev) => ev.preventDefault();

  onRepeatEmailChange = (event) => {
    const { value } = event.target;

    this.setState({
      repeatEmail: {
        value: value.trim(),
        isValid: this.state.email.value === value,
      },
    });
  };

  onShowLoginModal = () => {
    const { appActions: { setShowRegistrationModal } } = this.props;
    const { showModal, MODAL_KEYS } = ModalService;

    showModal(MODAL_KEYS.LOGIN_MODAL_V2,
      { handleOpenRegistrationModal: () => setShowRegistrationModal(true, RegistrationModalCaller.REGISTRATION_FORM) }
    );
  };

  render() {
    const { signUpType, user } = this.props;
    const {
      username,
      password,
      email,
      specialOffers,
      repeatEmail,
      showUsernameSuggestionPoppup,
      usernameSuggestions,
      tooltipOpen,
      formState,
    } = this.state;
    const { isGuestBilling } = UserStore;

    const isGoldRegistration = signUpType === SIGN_UP_TYPE.GOLD;
    const isFanclubRegistration = signUpType === SIGN_UP_TYPE.FANCLUB;
    const isDisabled = user && user.email && user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
    let btnLabel = translate('gold.gold.join.cam4.now');
    if (isFanclubRegistration) {
      btnLabel = translate('v2profile.join.fanclub');
    }
    if (isGoldRegistration) {
      btnLabel = translate('tipping.tipping.Create.Account');
    }

    return (
      <form
        className={styles.regForm}
        id={this.makeId(this.IDS.regForm)}
        onSubmit={this.onSubmitForm}
      >
        <div className={styles.inputAndTooltipContainer}>
          <Input
            errorText={this.getFieldErrorText('username')}
            id="registration-username"
            inputMargin="0 0 36px"
            label={translate('homepage.v2.studio.enter.a.username')}
            name="username"
            showSuccessMessage={true}
            valid={username.isValid}
            value={username.value}
            onChange={this.onUsernameChange}
          />
          {showUsernameSuggestionPoppup && (
            <Tooltip
              classes={{
                popper: `${styles.tooltipWrap} ${styles.suggestionBox} ${styles.suggestionBoxGold}`,
                tooltip: `${styles.tooltipPopUp} ${styles.tooltipPopUpUsername} ${styles.tooltipPopUpUsernamesStyle}`,
              }}
              enterTouchDelay={0}
              id="tooltipId"
              interactive={true}
              leaveTouchDelay={300000}
              open={tooltipOpen}
              placement="bottom-end"
              title={
                <Fragment>
                  <p className={styles.usernameTakenRed}>
                    {translate('profile.profile.register.username.taken')}
                  </p>

                  <span className={styles.usernameTakenSpan}>
                    {translate('profile.profile.register.username.taken.available')}
                  </span>

                  <ul className={styles.usernameList}>
                    {this.createUsernameSuggestionsList(usernameSuggestions)}
                  </ul>
                </Fragment>
              }
            >
              <InfoOutlinedIcon
                classes={{ root: isGoldRegistration ? styles.infoGoldIconUsername : styles.infoIconUsername }}
                onClick={this.toggleTooltip}
                onMouseEnter={this.toggleTooltip}
              />
            </Tooltip>
          )}
        </div>
        <div className={styles.inputAndTooltipContainer}>
          <Input
            errorText={password.errorMessage}
            id="registration-password"
            inputMargin="0 0 36px"
            label={translate('homepage.v2.studio.enter.a.password')}
            name="password"
            showSuccessMessage={true}
            type="password"
            valid={password.isValid}
            value={password.value}
            onChange={this.onPasswordInputChange}
          />

          <Tooltip
            classes={{
              popper: styles.tooltipWrap,
              tooltip: styles.tooltipPopUp,
            }}
            enterTouchDelay={0}
            leaveTouchDelay={300000}
            placement="top-end"
            title={
              <Fragment>
                <p className={styles.minChars}>{translate('profile.profile.minimum.characters')}</p>
                <span className={styles.mustContain}>{translate('profile.profile.must.contain')}</span>
                <span className={classNames(styles.mustContain, styles.specChars)}>
                  {translate('profile.profile.must.contain.special.character')}
                </span>
              </Fragment>
            }
          >
            <InfoOutlinedIcon
              classes={{ root: isGoldRegistration ? styles.infoGoldIcon : styles.infoIcon }}
            />
          </Tooltip>
        </div>
        <Input
          autoComplete="email"
          disabled={isDisabled}
          errorText={this.getFieldErrorText('email')}
          id="registration-email"
          inputMargin="0 0 36px"
          label={translate('homepage.v2.studio.enter.an.email.address')}
          name="email"
          showSuccessMessage={true}
          type="email"
          valid={email.isValid}
          value={email.value}
          onChange={this.onEmailChange}
        />

        {this.state.isRepeatEmailActive && (
          <Input
            autoComplete="repeatEmail"
            disabled={isDisabled}
            errorText={this.comparingEmails()}
            id="registration-repeat-email"
            inputMargin="0 0 36px"
            label={translate('homepage.v2.studio.enter.a.repeatEmail.address')}
            name="repeatEmail"
            showSuccessMessage={true}
            type="email"
            valid={repeatEmail.isValid}
            value={repeatEmail.value}
            onChange={this.onRepeatEmailChange}
            onPaste={this.onRepeatEmailPaste}
          />
        )}

        <div className={styles.wrapper}>
          {this.state.showCaptchaV2 && <div className={styles.captchaContainer}>{this.captchaV2View()}</div>}
          {this.state.showSubmitButton && (
            <div className={styles.buttonContainer}>
              <LoadingButton
                colour="orange"
                disabled={this.isBtnDisabled()}
                id={this.makeId(this.IDS.joinCAM4Btn)}
                isLoading={formState === 'loading'}
                type="submit"
              >
                {btnLabel}
              </LoadingButton>
              {isFanclubRegistration && !isGuestBilling && (
                <div>
                  <Button
                    className={styles.loginButton}
                    label={translate('profile.profile.log.in')}
                    onClick={this.onShowLoginModal}
                  />
                </div>
              )}
            </div>
          )}

          {!isGoldRegistration && (
            <CustomCheckbox
              checked={specialOffers}
              className={styles.checkboxLabel}
              classes={{
                root: styles.notChecked,
                checked: styles.checkbox,
              }}
              id="registration-consent-checkbox"
              label={translate('profile.profile.register.promotions.consent.new')}
              labelClass={styles.consentAndInfo}
              value="key"
              onChange={this.handlePromotions}
            />
          )}
          <p className={isGoldRegistration ? styles.goldTermsOfUse : styles.termsOfUse}>
            {translate('profile.v2.profile.register.termsofuse', {
              0: `<a href='${routeDefinitions.termsofuse}' target="_blank" rel="noopener noreferrer">`,
              1: '</a>',
              2: `<a href='${routeDefinitions.privacy}' target="_blank" rel="noopener noreferrer">`,
              3: '</a>',
            })}
          </p>
        </div>
      </form>
    );
  }
}

export default RegistrationForm;
